// src/onboarding/onboardingModel.js
//
// Every rule of the personalization workflow that can be stated without React, the network or
// copy — so the store, the poller, the gate, the banner, the badge and the wizard can never
// disagree. Tested on its own (onboardingModel.test.js).

import { MAX_LISTED_DOCUMENTS, isTextless } from '../context/documentUpload.js';

export const PERSONALIZATION_PATH = '/tenant-admin/personalization';

/** ⭐ Decision 3: the ONLY paths the post-login prompt may redirect FROM. Never a deep link. */
export const DEFAULT_LANDINGS = ['/', '/content-generator', '/dashboard'];

/** The views that prompt, show the "not personalized" banner and need the personalizability read. */
export const PROMPT_VIEWS = ['notPersonalized', 'failed', 'stale'];

// Decision 7: the stages we have copy for, in run order. ⛔ PENDING is "starting" (no step);
// anything else is "in corso" with no step. v196 (BE 571ac04): CUSTOMIZING is gone — a run no longer
// touches objectives or personas. A run in flight during the deploy may still read CUSTOMIZING: it then
// has no step and shows its label alone (`stages.CUSTOMIZING` is kept in the i18n files for that).
export const STAGES = ['INGESTING', 'BUILDING', 'EVALUATING', 'APPLYING'];
export const STAGE_TOTAL = STAGES.length;

export const POLL_RUNNING_MS = 10_000;
export const POLL_DEGRADED_MS = 30_000;
export const POLL_IDLE_MS = 300_000;
export const FOCUS_THROTTLE_MS = 30_000;
// v192: a SAFETY NET only. Since BE 178d473 a refused or crashed start is reported by the status read
// (`last_start_error`); what is left for this timeout is the rare start that leaves no trace at all
// (the API restarting between the 202 and the run row, the error record failing to write).
export const PENDING_START_TIMEOUT_MS = 300_000;

export const WEBSITE_MAX = 500;
export const SECTOR_MAX = 200;
export const DISPLAY_NAME_MAX = 100;

/** v196: the form's `documents` cap — since v197 the shared one (context/documentUpload.js). */
export const MAX_DOCUMENTS = MAX_LISTED_DOCUMENTS;

export const BRANDING_RULES = {
  logo: { exts: ['.svg', '.webp', '.png', '.jpg', '.jpeg'], maxBytes: 2 * 1024 * 1024 },
  favicon: { exts: ['.svg', '.ico', '.png'], maxBytes: 2 * 1024 * 1024 },
};

/** The status read → one view name (see the plan's "Views" table). */
export function viewOf(status) {
  if (!status) return 'loading';
  if (status.degraded) return 'unavailable';
  const p = status.personalization || {};
  switch (p.state) {
    case 'running': return p.stale ? 'stale' : 'running';
    case 'completed': return 'completed';
    case 'failed': return 'failed';
    case 'never_run': return 'notPersonalized';
    default: return 'unavailable';
  }
}

/** 1-based step of a known stage, 0 for PENDING, null for anything else. */
export function stageStep(stage) {
  if (stage === 'PENDING') return 0;
  const i = STAGES.indexOf(stage);
  return i === -1 ? null : i + 1;
}

/** Decision 6. */
export function pollDelay(status, { pendingStart = false, failed = false } = {}) {
  if (pendingStart) return POLL_RUNNING_MS;
  if (failed || !status || status.degraded) return POLL_DEGRADED_MS;
  return viewOf(status) === 'running' ? POLL_RUNNING_MS : POLL_IDLE_MS;
}

/** `personalization.last_attempt` (BE 178d473): the NEWEST run, whatever its outcome — or null. */
export function lastAttemptOf(status) {
  const la = status?.personalization?.last_attempt;
  return la && typeof la === 'object' ? la : null;
}

/**
 * `personalization.last_start_error` (BE 178d473): a start answered 202 and then refused or crashed
 * BEFORE its run existed — `{code, suggested_action, at}`, or null. The backend hides it by itself as
 * soon as a newer run exists.
 */
export function lastStartErrorOf(status) {
  const e = status?.personalization?.last_start_error;
  return e && typeof e === 'object' && e.code ? e : null;
}

/**
 * A personalized workspace whose NEWEST attempt failed: the top-level `state` stays `completed`
 * (the previous personalization is still in use), `last_attempt` is the failed re-run.
 */
export function lastAttemptFailed(status) {
  const p = status?.personalization;
  const la = lastAttemptOf(status);
  return !!p && p.state === 'completed' && !!la && la.state === 'failed' && la.run_id !== p.run_id;
}

/**
 * May a SECOND run be offered ("Esegui di nuovo", "Riprova" after a failed re-run)?
 * ⛔⛔ `allows_reruns` is a property of the PLAN, not of the workspace: on the free plan it is false
 * even on a workspace never personalized, whose FIRST run is allowed. So call this ONLY for the
 * second-run controls — never for Personalizza / Riprova on `never_run` / `failed`, nor for "Riavvia".
 * `null` / absent = unknown → offer the control and let the POST decide.
 */
export function canRunAgain(status) {
  return status?.personalization?.allows_reruns !== false;
}

/**
 * What the pending-start marker must remember BEFORE the POST: the newest run's id and the current
 * start error's `at` — so a CHANGE of either (compared as backend values, never against the browser's
 * clock) says what happened to OUR start.
 */
export function pendingBaseline(status) {
  return {
    prevAttemptRunId: lastAttemptOf(status)?.run_id ?? null,
    prevErrorAt: lastStartErrorOf(status)?.at ?? null,
  };
}

function endOf(attempt, next) {
  if (attempt.state === 'completed') return 'completed';
  return next?.personalization?.state === 'completed' ? 'rerunFailed' : 'failed';
}

/**
 * Decision 15, rewritten for BE 178d473 — what the NEW read says about the run the user was waiting
 * for. Every verdict comes from the backend's fields; the only guess left is the safety-net timeout.
 * @param {object|null} prevGood the last NON-degraded status
 * @param {object} next the fresh status
 * @param {{prevAttemptRunId: string|null, prevErrorAt: string|null, at: number}|null} pending
 * @param {number} now epoch ms
 * @returns {{outcome: null|'completed'|'failed'|'rerunFailed'|'startRefused'|'startUnconfirmed',
 *            code?: string, finished: boolean, clearPending: boolean}}
 */
export function detectOutcome(prevGood, next, pending, now) {
  const none = { outcome: null, finished: false, clearPending: false };
  if (!next || next.degraded) return none;
  const np = next.personalization || {};
  const attempt = lastAttemptOf(next);

  if (pending) {
    if (attempt && attempt.run_id && attempt.run_id !== pending.prevAttemptRunId) {
      if (attempt.state === 'running') return { outcome: null, finished: false, clearPending: true };
      return { outcome: endOf(attempt, next), finished: true, clearPending: true };
    }
    const err = lastStartErrorOf(next);
    if (err && err.at !== pending.prevErrorAt) {
      return { outcome: 'startRefused', code: err.code, finished: false, clearPending: true };
    }
    if (now - pending.at > PENDING_START_TIMEOUT_MS) {
      return { outcome: 'startUnconfirmed', finished: false, clearPending: true };
    }
    return none;
  }

  const pp = prevGood?.personalization;
  if (!pp || pp.state !== 'running' || np.state === 'running') return none;
  if (attempt && attempt.run_id === pp.run_id && attempt.state !== 'running') {
    return { outcome: endOf(attempt, next), finished: true, clearPending: false };
  }
  // The run we watched is not the newest any more (or the field is absent): the top-level state is
  // still the honest summary of how the workspace ended up.
  return { outcome: np.state === 'completed' ? 'completed' : 'failed', finished: true, clearPending: false };
}

/** Decision 10: the lazy brand-document read → true / false. */
export function personalizableFromDocument(doc) {
  return doc?.unlock_effective !== false;
}

/** Decision 10: a refused brand-document read → false (never personalizable) or null (unknown → fail open). */
export function personalizableFromError(err) {
  if (err?.code === 'BRAND_GUIDELINES_NOT_TENANT_OWNED') return false;
  if (err?.status === 404 || err?.code === 'RESOURCE_NOT_FOUND') return false;
  return null;
}

/** Is the store describing this workspace, with a usable status? */
export function describes(snapshot, tenantId) {
  return !!tenantId && snapshot?.tenantId === tenantId && !!snapshot.status;
}

/** Decisions 2–4 + 10: should the gate redirect now (before the per-login marker check)? */
export function shouldPrompt(snapshot, tenantId, pathname) {
  if (!describes(snapshot, tenantId)) return false;
  if (!DEFAULT_LANDINGS.includes(pathname)) return false;
  if (!PROMPT_VIEWS.includes(viewOf(snapshot.status))) return false;
  if (!snapshot.personalizableChecked) return false;
  return snapshot.personalizable !== false;
}

/** Decision 8: which banner to show (null = none). */
/**
 * The pages that carry the PersonalizationBadge next to their title (2026-09-30): there the banner's
 * standing lines ("in corso", "non personalizzato") only repeat the badge, so they are not shown. The
 * dismissable OUTCOME notice still is — the badge cannot say "the last attempt did not finish" or
 * "the start was not accepted" (after a failed re-run it simply reads "personalizzato").
 * ⚠️ Keep in sync with the badge's mounts (TenantAdminLayout, BrandVoicePage, TenantKnowledgeBasePage,
 * sections/KnowledgeBase, TenantPersonasPage).
 */
export const BADGE_PATH_PREFIXES = [
  '/tenant-admin/manage', '/tenant-admin/brand-voice',
  // 2026-10-01: the badge joined the white band of Knowledge Base (both mounts) and Buyer Personas.
  '/tenant-admin/knowledge-base', '/tenant-admin/personas', '/knowledge-base',
];

export function hasBadge(pathname) {
  return BADGE_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function bannerMode(snapshot, tenantId, pathname) {
  if (pathname === PERSONALIZATION_PATH) return null;
  if (!describes(snapshot, tenantId)) return null;
  const view = viewOf(snapshot.status);
  if (hasBadge(pathname)) return snapshot.outcome && view !== 'running' ? 'outcome' : null;
  if (view === 'running') return 'running';
  if (snapshot.outcome) return 'outcome';
  if (PROMPT_VIEWS.includes(view) && snapshot.personalizableChecked && snapshot.personalizable !== false) {
    return view; // 'notPersonalized' | 'failed' | 'stale'
  }
  return null;
}

/** Decision 9: which badge to show (null = none). */
export function badgeKind(snapshot, tenantId) {
  if (!describes(snapshot, tenantId)) return null;
  const view = viewOf(snapshot.status);
  if (PROMPT_VIEWS.includes(view) && snapshot.personalizable === false) return 'notAvailable';
  // v192 (D2): still personalized, but the newest re-run failed — said on the badge, not the banner.
  if (view === 'completed' && lastAttemptFailed(snapshot.status)) return 'completedLastFailed';
  return view; // 'unavailable' | 'running' | 'stale' | 'completed' | 'failed' | 'notPersonalized'
}

/** The wizard's panel. `step` is the page's local override ('form' after "Esegui di nuovo" / "Riavvia"). */
export function panelOf(snapshot, tenantId, step) {
  if (!describes(snapshot, tenantId)) return 'loading';
  const view = viewOf(snapshot.status);
  if (view === 'unavailable') return 'unavailable';
  if (view === 'running') return 'progress';
  if (snapshot.pendingStart) return 'starting';
  if (PROMPT_VIEWS.includes(view) && snapshot.personalizable === false) return 'notAvailable';
  if (step === 'form') return 'form';
  if (view === 'completed') return 'completed';
  if (view === 'stale') return 'stale';
  // v196 (BE 2ad8bdf): ONE form, decided from `personalization.state` alone (the status read's `form` block
  // now carries only `sector` — never a progress signal).
  return 'form'; // notPersonalized | failed
}

/** The form's website rule (client mirror of the backend's): http(s), 1–500 characters, no spaces. */
export function isValidWebsite(value) {
  const v = typeof value === 'string' ? value.trim() : '';
  return v.length > 0 && v.length <= WEBSITE_MAX && /^https?:\/\/[^\s]+$/i.test(v);
}

/** For the D6 comparison only: case, surrounding spaces and a trailing slash do not make a different site. */
export function sameSite(a, b) {
  const norm = (s) => (typeof s === 'string' ? s.trim().toLowerCase().replace(/\/+$/, '') : '');
  return norm(a) !== '' && norm(a) === norm(b);
}

/** The pre-fill: the connected site, or '' when there is none (placeholder / no file). */
export function connectedSiteOf(siteRead) {
  return siteRead && siteRead.site && !siteRead.is_sentinel ? siteRead.site : '';
}

/** v196: the website is OPTIONAL. null when empty or valid, 'invalid' otherwise. */
export function websiteProblem(value) {
  const v = typeof value === 'string' ? value.trim() : '';
  if (!v) return null;
  return isValidWebsite(v) ? null : 'invalid';
}

/** v196: at least one source of evidence — a valid website, or ≥ 1 ticked document. A sector alone is not one. */
export function hasSource(website, documents) {
  return isValidWebsite(website) || (Array.isArray(documents) && documents.length > 0);
}

/**
 * v196 — the contract's ONE extra warning: documents, NO website, on a plan that allows a single run.
 * `allows_reruns` null / absent = unknown → no warning (the same reading as `canRunAgain`).
 */
export function needsDocsOnlyWarning(status, website, documents) {
  const noSite = !(typeof website === 'string' && website.trim());
  return noSite && Array.isArray(documents) && documents.length > 0
    && status?.personalization?.allows_reruns === false;
}

/** v196: the sector recorded in the brand guidelines (`form.sector`), '' when none. */
export function recordedSectorOf(status) {
  const s = status?.form?.sector;
  return typeof s === 'string' ? s.trim() : '';
}

/**
 * v196: what to send as `sector` — ONLY a non-empty value that differs from the pre-fill. Omitting it keeps
 * the recorded sector (backend), which is also why an emptied field cannot clear it.
 * @returns {string|undefined}
 */
export function sectorToSend(prefill, value) {
  const v = typeof value === 'string' ? value.trim() : '';
  const p = typeof prefill === 'string' ? prefill.trim() : '';
  return v && v !== p ? v : undefined;
}

/** v196: every ticked document is known textless (and there is at least one). */
export function allTicksTextless(docs, selected) {
  if (!Array.isArray(selected) || selected.length === 0) return false;
  const byName = new Map((docs || []).map((d) => [d.filename, d]));
  return selected.every((n) => isTextless(byName.get(n)));
}

/** A branding file checked client-side: null when fine, else 'type' | 'size'. */
export function brandingProblem(slot, file) {
  const rule = BRANDING_RULES[slot];
  if (!rule || !file) return 'type';
  const dot = file.name.lastIndexOf('.');
  const ext = dot === -1 ? '' : file.name.slice(dot).toLowerCase();
  if (!rule.exts.includes(ext)) return 'type';
  if (file.size > rule.maxBytes) return 'size';
  return null;
}
